import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const HomePageContext = createContext();

export const useHomePageContext = () => {
    const context = useContext(HomePageContext);
    if (!context) {
        throw new Error('useHomePageContext must be used within HomePageProvider');
    }
    return context;
};

export const HomePageProvider = ({ children }) => {
    const location = useLocation();
    const [homePageState, setHomePageState] = useState({
        lastFetchTime: null,
        scrollPosition: 0,
        isPersisted: false,
        forceRefresh: false,
    });

    // Save scroll position when navigating away from homepage
    useEffect(() => {
        const handleScroll = () => {
            if (location.pathname === '/' || location.pathname === '/home') {
                setHomePageState(prev => ({
                    ...prev,
                    scrollPosition: window.scrollY
                }));
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [location.pathname]);

    // Restore scroll position when returning to homepage
    useEffect(() => {
        if ((location.pathname === '/' || location.pathname === '/home') && homePageState.scrollPosition > 0) {
            // Small delay to ensure content is rendered
            setTimeout(() => {
                window.scrollTo({
                    top: homePageState.scrollPosition,
                    behavior: 'smooth'
                });
            }, 100);
        }
    }, [location.pathname]);

    const updateLastFetchTime = useCallback(() => {
        setHomePageState(prev => ({
            ...prev,
            lastFetchTime: Date.now(),
            isPersisted: true
        }));
    }, []);

    const clearHomePageState = useCallback(() => {
        setHomePageState({
            lastFetchTime: null,
            scrollPosition: 0,
            isPersisted: false,
            forceRefresh: false,
        });
    }, []);

    const setForceRefresh = useCallback((value) => {
        setHomePageState(prev => ({
            ...prev,
            forceRefresh: value
        }));
    }, []);

    const shouldRefetchData = useCallback(() => {
        // Force refresh if explicitly set
        if (homePageState.forceRefresh) return true;
        
        // No data fetched yet
        if (!homePageState.lastFetchTime) return true;
        
        // Check if data is older than 10 minutes
        const TEN_MINUTES = 10 * 60 * 1000;
        const timeSinceLastFetch = Date.now() - homePageState.lastFetchTime;
        
        return timeSinceLastFetch > TEN_MINUTES;
    }, [homePageState.lastFetchTime, homePageState.forceRefresh]);

    const value = {
        homePageState,
        updateLastFetchTime,
        clearHomePageState,
        setForceRefresh,
        shouldRefetchData,
    };

    return (
        <HomePageContext.Provider value={value}>
            {children}
        </HomePageContext.Provider>
    );
};

export default HomePageContext;
